import { useEffect, useMemo, useState } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Kpi";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";

function StockCell({ region, stocks }) {
    const s = (stocks || []).find((r) => (r.region || "").toUpperCase() === region);
    if (!s) return <span className="text-[color:var(--bl-text-dim)]">—</span>;
    const qty = s.quantity_available || 0;
    const sold = s.quantity_sold_30_days || 0;
    const low = sold > 0 && qty < sold * 0.5;
    const over = sold > 0 && qty > sold * 3;
    return (
        <div className="leading-tight">
            <div className={`font-mono-num ${low ? "text-[color:var(--bl-danger)]" : over ? "text-[color:var(--bl-takealot-orange)]" : "text-[color:var(--bl-text)]"}`}>
                {formatInt(qty)}
            </div>
            <div className="text-[10px] text-[color:var(--bl-text-dim)]">sold30: {formatInt(sold)}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        buyable: "bg-[color:var(--bl-success)]/15 text-[color:var(--bl-success)]",
        not_buyable: "bg-[color:var(--bl-danger)]/15 text-[color:var(--bl-danger)]",
        disabled_by_seller: "bg-[color:var(--bl-text-dim)]/15 text-[color:var(--bl-text-dim)]",
        disabled_by_takealot: "bg-[color:var(--bl-danger)]/15 text-[color:var(--bl-danger)]",
    };
    return (
        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm ${map[status] || "bg-[color:var(--bl-text-dim)]/15 text-[color:var(--bl-text-dim)]"}`}>
            {status?.replace(/_/g, " ") || "—"}
        </span>
    );
}

export default function Products() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [edit, setEdit] = useState({}); // sku -> {cost, kg_per_unit, name_override}

    const load = () => {
        setLoading(true);
        api.get("/products")
            .then((r) => setProducts(r.data.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const term = q.toLowerCase();
        if (!term) return products;
        return products.filter(
            (o) =>
                (o.display_name || "").toLowerCase().includes(term) ||
                (o.title || "").toLowerCase().includes(term) ||
                (o.sku || "").toLowerCase().includes(term) ||
                String(o.offer_id || "").includes(term),
        );
    }, [products, q]);

    const saveRow = async (sku) => {
        const draft = edit[sku] || {};
        const payload = {};
        if (draft.cost !== undefined && draft.cost !== "") payload.cost = parseFloat(draft.cost);
        if (draft.kg_per_unit !== undefined && draft.kg_per_unit !== "") payload.kg_per_unit = parseFloat(draft.kg_per_unit);
        if (draft.name_override !== undefined) payload.name_override = draft.name_override;
        if (Object.keys(payload).length === 0) {
            toast.info("Nothing changed");
            return;
        }
        try {
            await api.put(`/products/${encodeURIComponent(sku)}`, payload);
            toast.success(`Saved ${sku}`);
            setEdit((s) => { const n = { ...s }; delete n[sku]; return n; });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <PageHeader title="Products" subtitle={`${products.length} products · set custom names, cost per unit (COGS) and kg per unit`}>
                <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--bl-text-dim)]" />
                    <input
                        data-testid="products-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, SKU, offer ID…"
                        className="bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] pl-8 pr-3 py-1.5 text-sm rounded-sm w-72 focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                    />
                </div>
            </PageHeader>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-x-auto shadow-sm">
                <table className="w-full text-sm min-w-[1200px]">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                        <tr>
                            <th className="text-left px-4 py-2">Product</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-right px-3 py-2">Price</th>
                            <th className="text-left px-3 py-2">Custom name</th>
                            <th className="text-right px-3 py-2">Kg / unit</th>
                            <th className="text-right px-3 py-2">Cost (R)</th>
                            <th className="text-right px-3 py-2">JHB</th>
                            <th className="text-right px-3 py-2">CPT</th>
                            <th className="text-right px-3 py-2">DBN</th>
                            <th className="text-right px-3 py-2">Views 30d</th>
                            <th className="text-right px-3 py-2">Conv %</th>
                            <th className="text-right px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody data-testid="products-table">
                        {loading && (
                            <tr><td colSpan={12} className="text-center text-[color:var(--bl-text-dim)] py-6">Loading…</td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={12} className="text-center text-[color:var(--bl-text-dim)] py-6">No products. Press Sync Takealot to pull them.</td></tr>
                        )}
                        {filtered.map((o) => {
                            const draft = edit[o.sku] || {};
                            const dirty = Object.keys(draft).length > 0;
                            return (
                                <tr key={o.offer_id} data-testid={`product-row-${o.sku}`} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                    <td className="px-4 py-3">
                                        <Link to={`/products/${encodeURIComponent(o.sku)}`} className="flex items-start gap-3 hover:opacity-80" data-testid={`product-link-${o.sku}`}>
                                            {o.image_url ? (
                                                <img src={o.image_url} alt="" className="w-10 h-10 object-cover rounded-sm border border-[color:var(--bl-border)] bg-white" />
                                            ) : (
                                                <div className="w-10 h-10 bg-[color:var(--bl-bg-2)] border border-[color:var(--bl-border)] rounded-sm" />
                                            )}
                                            <div className="min-w-0">
                                                <div className="text-[color:var(--bl-text)] line-clamp-2 leading-snug">
                                                    {o.display_name || o.title || o.sku}
                                                </div>
                                                <div className="font-mono-num text-[11px] text-[color:var(--bl-text-dim)] mt-0.5">
                                                    {o.sku} · #{o.offer_id}
                                                </div>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="px-3 py-3"><StatusBadge status={o.status} /></td>
                                    <td className="px-3 py-3 text-right font-mono-num">{formatZAR(o.selling_price)}</td>
                                    <td className="px-3 py-3">
                                        <input
                                            data-testid={`name-input-${o.sku}`}
                                            type="text"
                                            value={draft.name_override !== undefined ? draft.name_override : (o.name_override || "")}
                                            placeholder={o.title?.slice(0, 24) || ""}
                                            onChange={(e) => setEdit((s) => ({ ...s, [o.sku]: { ...s[o.sku], name_override: e.target.value } }))}
                                            className="w-44 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1 text-xs rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                                        />
                                    </td>
                                    <td className="px-3 py-3 text-right">
                                        <input
                                            data-testid={`kg-input-${o.sku}`}
                                            type="number" min="0" step="0.01"
                                            value={draft.kg_per_unit !== undefined ? draft.kg_per_unit : (o.kg_per_unit ?? "")}
                                            placeholder="0.00"
                                            onChange={(e) => setEdit((s) => ({ ...s, [o.sku]: { ...s[o.sku], kg_per_unit: e.target.value } }))}
                                            className="w-20 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1 text-right font-mono-num rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                                        />
                                    </td>
                                    <td className="px-3 py-3 text-right">
                                        <input
                                            data-testid={`cost-input-${o.sku}`}
                                            type="number" min="0" step="0.01"
                                            value={draft.cost !== undefined ? draft.cost : (o.cost ?? "")}
                                            placeholder="0.00"
                                            onChange={(e) => setEdit((s) => ({ ...s, [o.sku]: { ...s[o.sku], cost: e.target.value } }))}
                                            className="w-20 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1 text-right font-mono-num rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                                        />
                                    </td>
                                    <td className="px-3 py-3 text-right"><StockCell region="JHB" stocks={o.takealot_warehouse_stock} /></td>
                                    <td className="px-3 py-3 text-right"><StockCell region="CPT" stocks={o.takealot_warehouse_stock} /></td>
                                    <td className="px-3 py-3 text-right"><StockCell region="DBN" stocks={o.takealot_warehouse_stock} /></td>
                                    <td className="px-3 py-3 text-right font-mono-num">{formatInt(o.page_views_30_days)}</td>
                                    <td className="px-3 py-3 text-right font-mono-num">
                                        {o.conversion_percentage_30_days != null ? `${Number(o.conversion_percentage_30_days).toFixed(1)}%` : "—"}
                                    </td>
                                    <td className="px-3 py-3 text-right">
                                        {dirty && (
                                            <button
                                                data-testid={`product-save-${o.sku}`}
                                                onClick={() => saveRow(o.sku)}
                                                className="text-[10px] uppercase tracking-wider px-3 py-1 bg-[color:var(--bl-takealot-blue)] text-white rounded-sm hover:brightness-110"
                                            >Save</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
